import React from 'react';
import { View, ScrollView, StyleSheet, RefreshControl } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import type { StackNavigationProp } from '@react-navigation/stack';
import type { DesktopMainParamList } from '@/navigation/residents/desktop/DesktopNavigator';
import { PRIMITIVE_COLORS, SEMANTIC_COLORS } from '@/constants/residents/residentColors';
import { RESIDENTS_DESKTOP_COLORS } from '@/constants/residents/desktop/color';
import { useHomeScreen } from '@/hooks/residents/useHomeScreen';
import { useResponsive } from '@/hooks/common/useResponsive';

// Desktop tile components
import {
  DesktopPaymentTile,
  DesktopMaintenanceTile,
  DesktopBulletinTile,
  DesktopEventsTile,
  DesktopQuickLinksTile,
  DesktopWelcomeCard,
} from '@/components/residents/home/desktop';

const CONTENT_PADDING = 24;
const GRID_GAP = 16;

/**
 * DesktopHomeView - Desktop dashboard view for the home screen
 * 
 * Displays a grid of dashboard tiles with:
 * - Welcome card with user greeting
 * - Payment summary tile
 * - Maintenance requests tile
 * - Bulletins tile
 * - Upcoming events tile
 * - Quick links tile
 */
export const DesktopHomeView: React.FC = () => {
  const navigation = useNavigation<StackNavigationProp<DesktopMainParamList>>();
  const { width, isTablet } = useResponsive();
  
  const {
    user,
    balanceDue,
    openTickets,
    unreadBulletinsCount,
    validEvents,
    isPaymentLoading,
    isMaintenanceLoading,
    refreshing,
    shouldShowPayments,
    handleRefresh,
  } = useHomeScreen();

  // Desktop navigation handlers
  const handlePaymentsPress = () => navigation.navigate('Payments');
  const handleMakePayment = () => {
    if (user?.currentLeaseId) {
      navigation.navigate('PaymentSummaryModal', { 
        leaseId: user.currentLeaseId, 
        residentId: user.residentId 
      });
    } else {
      navigation.navigate('Payments');
    }
  };
  const handleMaintenancePress = () => navigation.navigate('Maintenance');
  const handleBulletinsPress = () => navigation.navigate('Bulletins');
  const handleEventsPress = () => navigation.navigate('Events');
  const handleMarketplacePress = () => navigation.navigate('Marketplace');
  const handleResourcesPress = () => navigation.navigate('Resources');
  const handleLinkedAccountsPress = () => navigation.navigate('LinkedAccounts');
  const handleMessagesPress = () => navigation.navigate('Messages');

  // Handle event press - navigate to Events screen (desktop doesn't have event detail modal)
  const handleEventPress = () => {
    navigation.navigate('Events');
  };

  // Calculate grid layout based on screen width
  const numColumns = isTablet ? 2 : 3;

  return (
    <ScrollView
      style={styles.container}
      contentContainerStyle={styles.contentContainer}
      showsVerticalScrollIndicator={false}
      refreshControl={
        <RefreshControl
          refreshing={refreshing}
          onRefresh={handleRefresh}
          tintColor={SEMANTIC_COLORS.text.primary}
        />
      }
    >
      {/* Welcome Card */}
      <DesktopWelcomeCard
        userName={user?.firstName}
        propertyName={user?.property?.name}
      />

      {/* Primary Dashboard Grid - Payment, Maintenance, Bulletins */}
      <View style={styles.primaryGrid}>
        {shouldShowPayments && (
          <DesktopPaymentTile
            balanceDue={balanceDue}
            isLoading={isPaymentLoading}
            onPress={handlePaymentsPress}
            onMakePayment={handleMakePayment}
          />
        )}
        
        <DesktopMaintenanceTile
          openTicketsCount={openTickets.length}
          isLoading={isMaintenanceLoading}
          onPress={handleMaintenancePress}
        />

        <DesktopBulletinTile
          unreadCount={unreadBulletinsCount}
          onPress={handleBulletinsPress}
        />
      </View>

      {/* Secondary Grid - Events and Quick Links */}
      <View style={styles.secondaryGrid}>
        <View style={styles.eventsColumn}>
          <DesktopEventsTile
            events={validEvents}
            onEventPress={handleEventPress}
            onViewAllPress={handleEventsPress}
          />
        </View>

        <View style={styles.quickLinksColumn}>
          <DesktopQuickLinksTile
            onMarketplacePress={handleMarketplacePress}
            onResourcesPress={handleResourcesPress}
            onLinkedAccountsPress={handleLinkedAccountsPress}
            onMessagesPress={handleMessagesPress}
          />
        </View>
      </View>
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: RESIDENTS_DESKTOP_COLORS.SECONDARY_BACKGROUND,
  },
  contentContainer: {
    padding: CONTENT_PADDING,
    paddingBottom: 60,
    gap: GRID_GAP,
  },
  primaryGrid: {
    flexDirection: 'row',
    gap: GRID_GAP,
  },
  secondaryGrid: {
    flexDirection: 'row',
    gap: GRID_GAP,
  },
  eventsColumn: {
    flex: 2,
  },
  quickLinksColumn: {
    flex: 1,
  },
});

export default DesktopHomeView;
